import { useEffect, useState, type JSX } from 'react'
import { api, stampWithZone, type Dashboard, type Tally, type TrendPoint } from '../../lib/api'
import { ErrorBox, Loading } from '../DataTable'

// How the register is spread, on the Reports screen's Breakdowns tab.
//
// # WHY THESE AND NOT THE WHOLE OVERVIEW
//
// The Programme overview was removed on 21 September 2026 (observation 3.18):
// its estate, risk and lateness cards repeated Home and the registers. What it
// alone showed was the register spread by business function, by registered
// country, by data classification and by certification status, and how the
// risk mix moved month by month. Those are here, read from the same
// GET /v1/dashboard it read, so nothing only it showed was lost.
//
// # WHY THE CHARTS ARE DRAWN BY HAND
//
// The console's content security policy allows scripts from itself and nowhere
// else, so a charting library would have to be bundled. None of these is more
// than a row of proportional bars, and a bar somebody can read the number off
// beats a canvas they cannot. Each is a list with a width, so a screen reader
// gets the figures and a printed page keeps them.

export const BREAKDOWNS_QUESTION = 'How is the register spread across functions, countries, data and certification?'

export function Breakdowns(): JSX.Element {
  const [answer, setAnswer] = useState<Dashboard | null>(null)
  const [error, setError] = useState<unknown>(null)

  useEffect(() => {
    api
      .dashboard()
      .then((a) => {
        setAnswer(a.dashboard)
        setError(null)
      })
      .catch(setError)
  }, [])

  if (error != null) return <ErrorBox error={error} what="The breakdowns" />
  if (!answer) return <Loading what="the breakdowns" />

  const d = answer
  return (
    <>
      <div className="chart-grid breakdowns-grid">
        <Chart heading="By business function" tally={d.businessFunctions} />
        <Chart heading="By registered country" tally={d.countries} />
        <Chart heading="By data classification" tally={d.dataClassifications} total={d.totalVendors} />
        <Chart heading="Certification status" tally={d.certificationStates} total={d.totalVendors} />
      </div>
      <RiskTrend points={d.riskTrend} />
      <p className="list-count" role="status">
        <strong>{d.totalVendors}</strong> vendors, counted {stampWithZone(d.computedAt)}
      </p>
    </>
  )
}

// How the risk mix moved, month by month.
//
// # WHY THIS IS BARS AND NOT A LINE
//
// A line chart of five bands is five lines, and the question people actually
// ask of a risk trend is whether the shape is getting worse, not what any one
// band did. A stacked bar per month answers the shape directly: if the dark
// part of the column is growing, the estate is deteriorating.
//
// # WHY IT SAYS SO WHEN THERE IS NOT ENOUGH HISTORY
//
// A trend drawn from one month is not a trend, and a single column with an
// axis around it invites somebody to read a direction into it.
function RiskTrend({ points }: { points: TrendPoint[] }): JSX.Element {
  const bands: { key: string; label: string; tone: string }[] = [
    { key: 'CRITICAL', label: 'Critical', tone: 'crit' },
    { key: 'HIGH', label: 'High', tone: 'high' },
    { key: 'MEDIUM', label: 'Medium', tone: 'med' },
    { key: 'LOW', label: 'Low', tone: 'low' },
    { key: 'INSUFFICIENT_EVIDENCE', label: 'Insufficient evidence', tone: 'none' },
    { key: 'UNSCORED', label: 'Not scored yet', tone: 'none' },
  ]
  const total = (p: TrendPoint) => bands.reduce((n, b) => n + (p.bands[b.key] ?? 0), 0)
  const tallest = Math.max(1, ...points.map(total))

  return (
    <section className="panel" aria-labelledby="risk-trend">
      <h2 id="risk-trend">Risk trend</h2>
      {points.length < 2 ? (
        <p className="muted">
          {points.length === 0
            ? 'Nothing to draw until a month ends with vendors on the register'
            : '1 month of history so far, no direction to read yet'}
        </p>
      ) : (
        <>
          <div className="trend">
            {points.map((p) => (
              <div className="trend-col" key={p.asOf}>
                <div className="trend-stack" style={{ height: Math.round((total(p) / tallest) * 120) + 'px' }}>
                  {bands.map((b) => {
                    const n = p.bands[b.key] ?? 0
                    if (n === 0) return null
                    return (
                      <span
                        key={b.key}
                        className={'trend-seg tone-' + b.tone}
                        style={{ flexGrow: n }}
                        title={n + ' ' + b.label + ' at ' + p.label}
                      />
                    )
                  })}
                </div>
                <span className="trend-label">{p.label}</span>
                <span className="trend-total">{total(p)}</span>
              </div>
            ))}
          </div>
          <div className="trend-key">
            {bands.map((b) => (
              <span key={b.key}>
                <i className={'tone-' + b.tone} /> {b.label}
              </span>
            ))}
          </div>
        </>
      )}
    </section>
  )
}

// A bar per value, widths proportional to the largest rather than to the total,
// because the question a distribution answers is which is biggest and by how
// much. The total is printed beside it where one exists, so a chart of six
// bars over three hundred vendors cannot be read as a chart of six vendors.
function Chart({ heading, tally, total }: { heading: string; tally: Tally[]; total?: number }): JSX.Element {
  const id = 'chart-' + heading.replace(/\s+/g, '-').toLowerCase()
  const max = tally.reduce((m, t) => Math.max(m, t.count), 0)
  const sum = tally.reduce((m, t) => m + t.count, 0)

  return (
    <section className="panel" aria-labelledby={id}>
      <div className="chart-head">
        <h2 id={id} style={{ margin: 0 }}>
          {heading}
        </h2>
      </div>
      {tally.length === 0 ? (
        <p className="muted" style={{ margin: 0 }}>
          Nothing recorded yet
        </p>
      ) : (
        <ul className="bars">
          {tally.map((t) => {
            const width = max > 0 ? Math.round((t.count / max) * 100) : 0
            return (
              <li key={t.key + t.label} className={t.count === 0 ? 'bar bar-zero' : 'bar'}>
                <span className="bar-row">
                  <span className="bar-label">{t.label}</span>
                  <span className="bar-track" aria-hidden="true">
                    <span className="bar-fill" style={{ width: width + '%' }} />
                  </span>
                  <span className="bar-value num">{t.count}</span>
                </span>
              </li>
            )
          })}
        </ul>
      )}
      {sum > 0 && (
        <p className="list-count">
          <strong>{sum}</strong> counted{total !== undefined && sum !== total ? ' of ' + total : ''}
        </p>
      )}
    </section>
  )
}
